import React, { useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useGroups, useOptions } from '../../../hooks/useCrmData';
import { getAccessToken } from '../../../authUtils';
import type { GroupType } from '../../../types/crm';
import {
  Modal, FormField, TextInput, Select, RadioGroup, Btn, ErrorBlock,
} from '../../ui/crm-ui';

interface Props {
  baseApiUrl: string;
  open: boolean;
  /**
   * If set, the modal forces type=family_group and pre-selects this corp group as the parent.
   * Used when "+ Family Group" is clicked from inside a corp-children view.
   */
  defaultParentCorpId?: number;
  /** Locks the type when set (e.g. 'family_group' from corp-children). */
  lockedType?: GroupType;
  onCreated: (newGroupId: number) => void;
  onClose: () => void;
}

function apiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

const CreateGroupModal: React.FC<Props> = ({
  baseApiUrl, open, defaultParentCorpId, lockedType, onCreated, onClose,
}) => {
  const [type, setType] = useState<GroupType>(lockedType ?? 'corporate_group');
  const [name, setName] = useState('');
  const [xpmCode, setXpmCode] = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [parentId, setParentId] = useState<string>(
    defaultParentCorpId != null ? String(defaultParentCorpId) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state every time the modal opens
  useEffect(() => {
    if (!open) return;
    setType(lockedType ?? 'corporate_group');
    setName('');
    setXpmCode('');
    setDepartmentId('');
    setParentId(defaultParentCorpId != null ? String(defaultParentCorpId) : '');
    setSubmitting(false);
    setError(null);
  }, [open, lockedType, defaultParentCorpId]);

  const departments = useOptions(baseApiUrl, open ? 'departments' : null);
  const corpGroups = useGroups(baseApiUrl, { type: 'corporate_group' });

  const showParentPicker = type === 'family_group';
  const parentLocked = defaultParentCorpId != null;

  async function handleSubmit() {
    setError(null);
    if (!name.trim()) { setError('Name is required.'); return; }
    const token = await getAccessToken();
    if (!token) { setError('Not authenticated. Please sign in and try again.'); return; }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        type,
      };
      if (xpmCode.trim()) payload.xpm_code = xpmCode.trim();
      if (departmentId) payload.department_id = Number(departmentId);
      if (showParentPicker && parentId) payload.parent_id = Number(parentId);

      const res = await axios.post<{ id: number }>(`${baseApiUrl}/api/groups`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(res.data.id);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={lockedType === 'family_group' ? 'Add Family Group' : 'Add Client Group'}
      footer={
        <>
          <Btn onClick={onClose} disabled={submitting}>Cancel</Btn>
          <Btn primary onClick={handleSubmit} disabled={submitting || !name.trim()}>
            {submitting ? 'Creating…' : 'Create'}
          </Btn>
        </>
      }
    >
      {!lockedType && (
        <FormField label="Type" required>
          <RadioGroup<GroupType>
            value={type}
            onChange={setType}
            options={[
              { label: 'Corporate Group', value: 'corporate_group', description: 'Top-level umbrella that can contain family groups and jobs.' },
              { label: 'Family Group',    value: 'family_group',    description: 'A family unit. Can sit standalone or inside a corporate group.' },
            ]}
          />
        </FormField>
      )}

      <FormField label="Name" required>
        <TextInput
          value={name}
          onChange={setName}
          placeholder={type === 'corporate_group' ? 'e.g. Smith Holdings' : 'e.g. Smith Family'}
          autoFocus
        />
      </FormField>

      <FormField label="XPM Code">
        <TextInput value={xpmCode} onChange={setXpmCode} placeholder="Optional" />
      </FormField>

      <FormField label="Department">
        <Select
          value={departmentId}
          onChange={setDepartmentId}
          options={(departments.data || []).map((d) => ({ label: d.name, value: d.id }))}
          placeholder="(none)"
          disabled={departments.loading}
        />
      </FormField>

      {showParentPicker && (
        <FormField
          label="Parent corporate group"
          hint={parentLocked ? 'Pre-selected based on the group you’re inside.' : 'Optional — leave blank for a standalone family group.'}
        >
          <Select
            value={parentId}
            onChange={setParentId}
            options={(corpGroups.data || []).map((g) => ({ label: g.name, value: g.id }))}
            placeholder="(none — standalone)"
            disabled={parentLocked || corpGroups.loading}
          />
        </FormField>
      )}

      {error && <div style={{ marginTop: 12 }}><ErrorBlock message={error} /></div>}
    </Modal>
  );
};

export default CreateGroupModal;
